import React, { useState, useEffect, useCallback } from 'react';
import { 
  Upload, 
  Trash2, 
  GripVertical, 
  Image as ImageIcon, 
  RefreshCw,
  ChevronDown,
  X,
  Save,
  ExternalLink,
  Star,
  Wand2,
  CheckCircle,
  AlertCircle,
  Loader2
} from 'lucide-react';
import { Button } from './ui/button';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';
const ADMIN_KEY = 'jnm-admin-2024';

const ProductImageManager = () => {
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [gallery, setGallery] = useState([]);
  const [mainImage, setMainImage] = useState('');
  const [saving, setSaving] = useState(false);
  // Background removal feature removed — admin uploads pre-processed images.
  const [uploadingMain, setUploadingMain] = useState(false);
  const [uploadingGallery, setUploadingGallery] = useState(false);
  const [processingFiles, setProcessingFiles] = useState([]);
  const [draggedIndex, setDraggedIndex] = useState(null);

  const fetchProducts = useCallback(async () => {
    try {
      setLoading(true);
      const res = await fetch(`${API_URL}/api/admin/all-products-for-gallery`, {
        headers: { 'X-Admin-Key': ADMIN_KEY }
      });
      if (!res.ok) throw new Error('Fehler beim Laden');
      const data = await res.json();
      setProducts(data.items || []);
    } catch (error) {
      toast.error('Fehler beim Laden der Produkte');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchProducts(); }, [fetchProducts]);

  const loadProduct = (product) => {
    setSelectedProduct(product);
    setGallery(product.gallery || []);
    setMainImage(product.image || '');
  };

  // Upload main image (Hauptbild)
  const handleMainImageUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 20 * 1024 * 1024) {
      toast.error('Datei zu groß (max. 20MB)');
      return;
    }

    try {
      setUploadingMain(true);
      const formData = new FormData();
      formData.append('file', file);

      const url = `${API_URL}/api/admin/products/${selectedProduct.slug}/main-image?remove_bg=false`;
      const res = await fetch(url, {
        method: 'POST',
        headers: { 'X-Admin-Key': ADMIN_KEY },
        body: formData
      });

      if (!res.ok) {
        const err = await res.json();
        throw new Error(err.detail || 'Upload fehlgeschlagen');
      }

      const data = await res.json();
      setMainImage(data.url);
      
      // Update local product state
      setProducts(prev => prev.map(p => 
        p.slug === selectedProduct.slug ? { ...p, image: data.url } : p
      ));
      setSelectedProduct(prev => ({ ...prev, image: data.url }));
      
      toast.success('Hauptbild gespeichert');
    } catch (error) {
      toast.error(error.message || 'Upload fehlgeschlagen');
    } finally {
      setUploadingMain(false);
      e.target.value = '';
    }
  };

  // Upload gallery images (single or multiple)
  const handleGalleryUpload = async (e) => {
    const files = Array.from(e.target.files || []);
    if (files.length === 0) return;

    const maxSlots = 20 - gallery.length;
    if (files.length > maxSlots) {
      toast.error(`Maximal noch ${maxSlots} Bilder möglich (20 pro Produkt)`);
      return;
    }

    setUploadingGallery(true);
    const initialProcessing = files.map((f, i) => ({
      id: `${Date.now()}_${i}`,
      name: f.name,
      status: 'waiting'
    }));
    setProcessingFiles(initialProcessing);

    for (let i = 0; i < files.length; i++) {
      const file = files[i];
      if (file.size > 20 * 1024 * 1024) {
        setProcessingFiles(prev => prev.map((pf, idx) => 
          idx === i ? { ...pf, status: 'error', message: 'Zu groß' } : pf
        ));
        continue;
      }

      setProcessingFiles(prev => prev.map((pf, idx) => 
        idx === i ? { ...pf, status: 'processing' } : pf
      ));

      try {
        const formData = new FormData();
        formData.append('file', file);

        const url = `${API_URL}/api/admin/products/${selectedProduct.slug}/gallery/upload?remove_bg=false`;
        const res = await fetch(url, {
          method: 'POST',
          headers: { 'X-Admin-Key': ADMIN_KEY },
          body: formData
        });

        if (!res.ok) {
          const err = await res.json();
          throw new Error(err.detail || 'Fehlgeschlagen');
        }

        const data = await res.json();
        setGallery(prev => [...prev, data.url]);

        const savedKB = data.original_size_kb && data.processed_size_kb 
          ? ` (${Math.round(data.original_size_kb)}KB → ${Math.round(data.processed_size_kb)}KB)`
          : '';

        setProcessingFiles(prev => prev.map((pf, idx) => 
          idx === i ? { ...pf, status: 'done', message: savedKB } : pf
        ));
      } catch (error) {
        setProcessingFiles(prev => prev.map((pf, idx) => 
          idx === i ? { ...pf, status: 'error', message: error.message } : pf
        ));
      }
    }

    setUploadingGallery(false);
    setTimeout(() => setProcessingFiles([]), 4000);
    e.target.value = '';
  };

  const handleRemoveGalleryImage = (index) => {
    setGallery(prev => prev.filter((_, i) => i !== index));
  };

  const handleDragStart = (index) => setDraggedIndex(index);
  const handleDragOver = (e, index) => {
    e.preventDefault();
    if (draggedIndex === null || draggedIndex === index) return;
    const newGallery = [...gallery];
    const item = newGallery[draggedIndex];
    newGallery.splice(draggedIndex, 1);
    newGallery.splice(index, 0, item);
    setGallery(newGallery);
    setDraggedIndex(index);
  };
  const handleDragEnd = () => setDraggedIndex(null);

  const handleSaveGallery = async () => {
    if (!selectedProduct) return;
    try {
      setSaving(true);
      const endpoint = selectedProduct.type === 'accessory' 
        ? `/api/admin/accessories/${selectedProduct.slug}/gallery`
        : `/api/admin/products/${selectedProduct.slug}/gallery`;

      const res = await fetch(`${API_URL}${endpoint}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', 'X-Admin-Key': ADMIN_KEY },
        body: JSON.stringify({ gallery })
      });

      if (!res.ok) throw new Error('Speichern fehlgeschlagen');

      setProducts(prev => prev.map(p => 
        p.slug === selectedProduct.slug ? { ...p, gallery } : p
      ));
      setSelectedProduct(prev => ({ ...prev, gallery }));
      toast.success(`Galerie gespeichert (${gallery.length} Bilder)`);
    } catch (error) {
      toast.error(error.message);
    } finally {
      setSaving(false);
    }
  };

  // Group products by category
  const categoryOrder = ['Mini-Bagger', 'Radlader', 'Gabelstapler', 'Rüttelplatte', 'Stampfer', 'Zubehör'];
  const groupedProducts = products.reduce((acc, product) => {
    const cat = product.category || 'Sonstige';
    if (!acc[cat]) acc[cat] = [];
    acc[cat].push(product);
    return acc;
  }, {});
  const sortedCategories = Object.keys(groupedProducts).sort((a, b) => {
    const iA = categoryOrder.indexOf(a);
    const iB = categoryOrder.indexOf(b);
    return (iA === -1 ? 99 : iA) - (iB === -1 ? 99 : iB);
  });

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <RefreshCw className="w-8 h-8 animate-spin text-gray-400" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="product-image-manager">
      {/* Header */}
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h2 className="text-xl font-bold text-gray-900">Bildverwaltung</h2>
          <p className="text-sm text-gray-500 mt-1">Hauptbilder &amp; Galerien verwalten. Bilder werden unverändert übernommen.</p>
        </div>
        <div className="flex items-center gap-3">
          <Button variant="outline" size="sm" onClick={fetchProducts} data-testid="refresh-products">
            <RefreshCw className="w-4 h-4 mr-2" />
            Aktualisieren
          </Button>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
        {/* Product List - Left Panel */}
        <Card className="lg:col-span-3">
          <CardHeader className="pb-3">
            <CardTitle className="text-base">Produkt auswählen</CardTitle>
          </CardHeader>
          <CardContent className="max-h-[700px] overflow-y-auto space-y-4 p-3">
            {sortedCategories.map((category) => (
              <div key={category}>
                <h3 className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-2 flex items-center gap-1">
                  <ChevronDown className="w-3 h-3" />
                  {category} ({groupedProducts[category].length})
                </h3>
                <div className="space-y-1">
                  {groupedProducts[category].map((product) => (
                    <button
                      key={product.slug}
                      onClick={() => loadProduct(product)}
                      data-testid={`select-product-${product.slug}`}
                      className={`w-full text-left px-3 py-2 rounded-lg text-sm transition-all flex items-center gap-2 ${
                        selectedProduct?.slug === product.slug
                          ? 'bg-red-50 text-red-700 border border-red-200 shadow-sm'
                          : 'hover:bg-gray-50 border border-transparent'
                      }`}
                    >
                      {product.image ? (
                        <img src={product.image} alt="" className="w-9 h-9 object-contain rounded bg-gray-50 border" />
                      ) : (
                        <div className="w-9 h-9 bg-gray-100 rounded flex items-center justify-center border">
                          <ImageIcon className="w-4 h-4 text-gray-400" />
                        </div>
                      )}
                      <div className="flex-1 min-w-0">
                        <div className="font-medium truncate text-xs">{product.name}</div>
                        <div className="text-[10px] text-gray-400">
                          {(product.gallery || []).length}/20 Bilder
                        </div>
                      </div>
                      {product.image && (
                        <Star className="w-3 h-3 text-amber-400 flex-shrink-0" />
                      )}
                    </button>
                  ))}
                </div>
              </div>
            ))}
          </CardContent>
        </Card>

        {/* Editor - Right Panel */}
        <div className="lg:col-span-9 space-y-6">
          {selectedProduct ? (
            <>
              {/* ===== HAUPTBILD (Main Image) Section ===== */}
              <Card data-testid="main-image-section">
                <CardHeader className="pb-3">
                  <div className="flex items-center justify-between">
                    <CardTitle className="text-base flex items-center gap-2">
                      <Star className="w-4 h-4 text-amber-500" />
                      Hauptbild — {selectedProduct.name}
                    </CardTitle>
                  </div>
                  <p className="text-xs text-gray-500">Das Hauptbild wird als Titelbild auf der Website angezeigt</p>
                </CardHeader>
                <CardContent>
                  <div className="flex items-start gap-6 flex-wrap">
                    {/* Current Main Image Preview */}
                    <div className="w-48 h-48 bg-gray-50 border-2 border-dashed border-gray-200 rounded-xl flex items-center justify-center overflow-hidden">
                      {uploadingMain ? (
                        <div className="text-center p-4">
                          <Loader2 className="w-8 h-8 animate-spin text-red-500 mx-auto mb-2" />
                          <p className="text-xs text-gray-500">
                            Wird hochgeladen...
                          </p>
                        </div>
                      ) : mainImage ? (
                        <img src={mainImage} alt="Hauptbild" className="max-w-full max-h-full object-contain" />
                      ) : (
                        <div className="text-center p-4">
                          <ImageIcon className="w-10 h-10 text-gray-300 mx-auto mb-2" />
                          <p className="text-xs text-gray-400">Kein Hauptbild</p>
                        </div>
                      )}
                    </div>

                    {/* Upload Controls */}
                    <div className="flex-1 space-y-3">
                      <label className="cursor-pointer" data-testid="upload-main-image">
                        <input
                          type="file"
                          accept="image/*"
                          onChange={handleMainImageUpload}
                          disabled={uploadingMain}
                          className="hidden"
                        />
                        <div className="border-2 border-dashed border-gray-300 rounded-xl p-6 text-center hover:border-red-400 hover:bg-red-50/30 transition-colors">
                          <Upload className="w-6 h-6 text-gray-400 mx-auto mb-2" />
                          <p className="text-sm font-medium text-gray-700">Hauptbild hochladen</p>
                          <p className="text-xs text-gray-400 mt-1">
                            JPEG, PNG, WebP — max. 20MB
                          </p>
                        </div>
                      </label>
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* ===== GALERIE Section ===== */}
              <Card data-testid="gallery-section">
                <CardHeader className="pb-3">
                  <div className="flex items-center justify-between">
                    <div>
                      <CardTitle className="text-base flex items-center gap-2">
                        <ImageIcon className="w-4 h-4 text-blue-500" />
                        Galerie — {selectedProduct.name}
                      </CardTitle>
                      <p className="text-xs text-gray-500 mt-1">
                        {gallery.length}/20 Bilder — Drag & Drop zum Sortieren
                      </p>
                    </div>
                    <Button 
                      onClick={handleSaveGallery} 
                      disabled={saving}
                      className="bg-green-600 hover:bg-green-700"
                      data-testid="save-gallery"
                    >
                      {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
                      Reihenfolge speichern
                    </Button>
                  </div>
                </CardHeader>
                <CardContent className="space-y-4">
                  {/* Upload Area */}
                  <label className="cursor-pointer block" data-testid="upload-gallery-images">
                    <input
                      type="file"
                      accept="image/*"
                      multiple
                      onChange={handleGalleryUpload}
                      disabled={uploadingGallery}
                      className="hidden"
                    />
                    <div className="border-2 border-dashed border-gray-300 rounded-xl p-4 text-center hover:border-blue-400 hover:bg-blue-50/30 transition-colors">
                      <Upload className="w-5 h-5 text-gray-400 mx-auto mb-1" />
                      <p className="text-sm font-medium text-gray-700">Galerie-Bilder hochladen</p>
                      <p className="text-xs text-gray-400">Mehrere Bilder gleichzeitig möglich — max. 20MB pro Bild</p>
                    </div>
                  </label>

                  {/* Processing Status */}
                  {processingFiles.length > 0 && (
                    <div className="bg-gray-50 rounded-lg p-3 space-y-2" data-testid="processing-status">
                      <p className="text-xs font-semibold text-gray-600 uppercase tracking-wider">Verarbeitung</p>
                      {processingFiles.map((pf) => (
                        <div key={pf.id} className="flex items-center gap-2 text-sm">
                          {pf.status === 'waiting' && <Loader2 className="w-4 h-4 text-gray-400" />}
                          {pf.status === 'processing' && <Loader2 className="w-4 h-4 animate-spin text-purple-500" />}
                          {pf.status === 'done' && <CheckCircle className="w-4 h-4 text-green-500" />}
                          {pf.status === 'error' && <AlertCircle className="w-4 h-4 text-red-500" />}
                          <span className={`truncate flex-1 ${pf.status === 'error' ? 'text-red-600' : 'text-gray-700'}`}>
                            {pf.name}
                          </span>
                          {pf.status === 'processing' && (
                            <span className="text-xs text-blue-500">Wird hochgeladen...</span>
                          )}
                          {pf.message && (
                            <span className="text-xs text-gray-400">{pf.message}</span>
                          )}
                        </div>
                      ))}
                    </div>
                  )}

                  {/* Gallery Grid */}
                  {gallery.length > 0 ? (
                    <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 xl:grid-cols-5 gap-3">
                      {gallery.map((url, index) => (
                        <div
                          key={`${url}-${index}`}
                          draggable
                          onDragStart={() => handleDragStart(index)}
                          onDragOver={(e) => handleDragOver(e, index)}
                          onDragEnd={handleDragEnd}
                          data-testid={`gallery-image-${index}`}
                          className={`relative group bg-white border rounded-lg overflow-hidden cursor-move transition-all ${
                            draggedIndex === index ? 'ring-2 ring-blue-500 opacity-50' : 'hover:shadow-md'
                          }`}
                        >
                          <div className="aspect-square flex items-center justify-center bg-gray-50">
                            <img
                              src={url}
                              alt={`Galerie ${index + 1}`}
                              className="max-w-full max-h-full object-contain"
                              onError={(e) => {
                                e.target.style.display = 'none';
                                if (e.target.nextSibling) e.target.nextSibling.style.display = 'flex';
                              }}
                            />
                            <div className="hidden items-center justify-center absolute inset-0 bg-gray-100">
                              <ImageIcon className="w-8 h-8 text-gray-400" />
                            </div>
                          </div>
                          
                          {/* Hover Overlay */}
                          <div className="absolute inset-0 bg-black/60 opacity-0 group-hover:opacity-100 transition-opacity flex flex-col items-center justify-center gap-2">
                            <div className="flex items-center gap-1 text-white text-xs">
                              <GripVertical className="w-4 h-4" />
                              <span>#{index + 1}</span>
                            </div>
                            <div className="flex gap-1">
                              <a href={url} target="_blank" rel="noopener noreferrer"
                                className="p-1.5 bg-white/20 rounded hover:bg-white/30"
                                title="Vollbild öffnen">
                                <ExternalLink className="w-4 h-4 text-white" />
                              </a>
                              <button
                                onClick={() => handleRemoveGalleryImage(index)}
                                className="p-1.5 bg-red-500/80 rounded hover:bg-red-600"
                                title="Entfernen"
                                data-testid={`remove-gallery-image-${index}`}
                              >
                                <Trash2 className="w-4 h-4 text-white" />
                              </button>
                            </div>
                          </div>
                          
                          <div className="absolute top-1 left-1 bg-black/50 text-white text-[10px] px-1.5 py-0.5 rounded">
                            {index + 1}
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div className="text-center py-10 bg-gray-50 rounded-lg border-2 border-dashed border-gray-200">
                      <ImageIcon className="w-10 h-10 text-gray-300 mx-auto mb-2" />
                      <p className="text-gray-500 text-sm">Keine Galerie-Bilder</p>
                      <p className="text-xs text-gray-400 mt-1">Laden Sie Ihre vorbereiteten Bilder hoch.</p>
                    </div>
                  )}
                </CardContent>
              </Card>
            </>
          ) : (
            <Card>
              <CardContent className="py-20 text-center">
                <ImageIcon className="w-16 h-16 text-gray-200 mx-auto mb-4" />
                <p className="text-gray-500 text-lg font-medium">Produkt auswählen</p>
                <p className="text-gray-400 text-sm mt-2">
                  Wählen Sie ein Produkt aus der Liste,<br />um Hauptbild und Galerie zu verwalten.
                </p>
              </CardContent>
            </Card>
          )}
        </div>
      </div>
    </div>
  );
};

export default ProductImageManager;
